import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import { formatDateTimeDe } from "@/lib/heroDocumentReports";
import {
  createDokumentZahlung,
  deleteDokumentZahlung,
  formatBelegdatum,
  updateDokumentZahlung,
  ZAHLUNGSSTATUS_LABELS,
  type DokumentDetail,
  type DokumentZahlung,
  type DokumentZahlungProtokoll,
  type ZahlungProtokollAktion,
  type Zahlungsstatus,
} from "@/lib/dokumente";
import { notifyMahnwesenBadgeRefresh } from "@/lib/navBadges";
import { toast } from "@/lib/toast";

function todayIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function parseBetragInput(raw: string): number | null {
  const normalized = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0) return null;
  return roundMoney(n);
}

function moneyInput(value: number): string {
  return String(value).replace(".", ",");
}

function dateInput(value?: string | null): string {
  return (value || "").slice(0, 10);
}

/** Zahlungen werden brutto erfasst – Netto anteilig über Dokument-Summen. */
function bruttoToNetto(brutto: number, summeBrutto: number, summeNetto: number): number | null {
  if (!(brutto > 0) || !(summeBrutto > 0) || !(summeNetto > 0)) return null;
  return roundMoney(brutto * (summeNetto / summeBrutto));
}

const AKTION_LABEL: Record<ZahlungProtokollAktion, string> = {
  erfasst: "Erfasst",
  geaendert: "Geändert",
  geloescht: "Gelöscht",
};

const AKTION_BADGE: Record<ZahlungProtokollAktion, string> = {
  erfasst: "bg-status-green/15 text-status-green",
  geaendert: "bg-status-orange/15 text-status-orange",
  geloescht: "bg-status-red/10 text-status-red",
};

function personLabel(name?: string | null, login?: string | null): string {
  return (name || login || "").trim() || "Unbekannt";
}

function protocolDetail(entry: DokumentZahlungProtokoll, currency: string): string {
  const amount =
    entry.betrag != null ? formatCurrency(Number(entry.betrag), currency) : "";
  const previous =
    entry.vorher_betrag != null ? formatCurrency(Number(entry.vorher_betrag), currency) : "";
  const parts: string[] = [];
  if (entry.aktion === "geaendert" && previous && amount && previous !== amount) {
    parts.push(`${previous} → ${amount}`);
  } else if (amount) {
    parts.push(amount);
  }
  const paidOn = dateInput(entry.bezahlt_am);
  const previousPaid = dateInput(entry.vorher_bezahlt_am);
  if (entry.aktion === "geaendert" && previousPaid && paidOn && previousPaid !== paidOn) {
    parts.push(`Zahlungsdatum ${formatBelegdatum(previousPaid)} → ${formatBelegdatum(paidOn)}`);
  } else if (paidOn) {
    parts.push(`Zahlungsdatum ${formatBelegdatum(paidOn)}`);
  }
  if (entry.referenz?.trim()) parts.push(`Ref. ${entry.referenz.trim()}`);
  return parts.join(" · ");
}

export function DocumentPaymentsDialog({
  open,
  doc,
  onClose,
  onUpdated,
  zIndexClass,
}: {
  open: boolean;
  doc: DokumentDetail;
  onClose: () => void;
  onUpdated: (next: DokumentDetail) => void;
  zIndexClass?: string;
}) {
  const currency = doc.waehrung || "EUR";
  const summeNetto = Number(doc.summe_netto || 0);
  const summeSteuer = Number(doc.summe_steuer || 0);
  const summeBrutto = Number(doc.summe_brutto || 0);
  const summeBezahlt = Number(doc.summe_bezahlt || 0);
  const summeOffen = Math.max(
    roundMoney(Number(doc.summe_offen ?? summeBrutto - summeBezahlt)),
    0,
  );
  const summeBezahltNetto = bruttoToNetto(summeBezahlt, summeBrutto, summeNetto);
  const summeOffenNetto = bruttoToNetto(summeOffen, summeBrutto, summeNetto);
  const zahlungsstatus = (doc.zahlungsstatus || "offen") as Zahlungsstatus;
  const zahlungen = doc.zahlungen || [];
  const protokoll = doc.zahlung_protokoll || [];
  const isPaid = zahlungsstatus === "bezahlt" || doc.status === "abgerechnet";

  const [betrag, setBetrag] = useState("");
  const [bezahltAm, setBezahltAm] = useState(todayIsoDate());
  const [referenz, setReferenz] = useState("");
  const [notiz, setNotiz] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBetrag, setEditBetrag] = useState("");
  const [editBezahltAm, setEditBezahltAm] = useState("");
  const [editReferenz, setEditReferenz] = useState("");
  const [editNotiz, setEditNotiz] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBezahltAm(todayIsoDate());
    setReferenz("");
    setNotiz("");
    setBetrag(summeOffen > 0 ? moneyInput(summeOffen) : "");
    setShowComposer(summeOffen > 0);
    setEditingId(null);
    setConfirmDeleteId(null);
  }, [open, doc.id, summeOffen]);

  const parsedBetrag = useMemo(() => parseBetragInput(betrag), [betrag]);
  const parsedBetragNetto = useMemo(
    () => (parsedBetrag != null ? bruttoToNetto(parsedBetrag, summeBrutto, summeNetto) : null),
    [parsedBetrag, summeBrutto, summeNetto],
  );
  const parsedEditBetrag = useMemo(() => parseBetragInput(editBetrag), [editBetrag]);
  const canSave = parsedBetrag != null && !saving;
  const canSaveEdit = parsedEditBetrag != null && !saving;

  const lastChangeByZahlung = useMemo(() => {
    const map = new Map<number, DokumentZahlungProtokoll>();
    for (const entry of protokoll) {
      if (entry.aktion !== "geaendert" || entry.zahlung_id == null) continue;
      if (!map.has(entry.zahlung_id)) map.set(entry.zahlung_id, entry);
    }
    return map;
  }, [protokoll]);

  function applyUpdated(next: DokumentDetail, message: string) {
    onUpdated(next);
    notifyMahnwesenBadgeRefresh();
    toast.success(message);
  }

  async function handleCreate() {
    if (parsedBetrag == null) {
      setError("Bitte einen gültigen Betrag größer 0 eingeben.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await createDokumentZahlung(doc.id, {
        betrag: parsedBetrag,
        bezahlt_am: bezahltAm || null,
        referenz: referenz.trim() || null,
        notiz: notiz.trim() || null,
      });
      applyUpdated(
        next,
        next.zahlungsstatus === "bezahlt"
          ? "Zahlung erfasst – Rechnung ist bezahlt."
          : "Teilzahlung erfasst.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Zahlung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(zahlung: DokumentZahlung) {
    setEditingId(zahlung.id);
    setConfirmDeleteId(null);
    setEditBetrag(moneyInput(Number(zahlung.betrag || 0)));
    setEditBezahltAm(dateInput(zahlung.bezahlt_am) || todayIsoDate());
    setEditReferenz(zahlung.referenz || "");
    setEditNotiz(zahlung.notiz || "");
    setError(null);
  }

  async function handleUpdate(zahlungId: number) {
    if (parsedEditBetrag == null) {
      setError("Bitte einen gültigen Betrag größer 0 eingeben.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await updateDokumentZahlung(doc.id, zahlungId, {
        betrag: parsedEditBetrag,
        bezahlt_am: editBezahltAm || null,
        referenz: editReferenz.trim() || null,
        notiz: editNotiz.trim() || null,
      });
      setEditingId(null);
      applyUpdated(next, "Zahlung geändert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Zahlung konnte nicht geändert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(zahlungId: number) {
    setDeletingId(zahlungId);
    setError(null);
    try {
      const next = await deleteDokumentZahlung(doc.id, zahlungId);
      setConfirmDeleteId(null);
      if (editingId === zahlungId) setEditingId(null);
      applyUpdated(next, "Zahlung gelöscht.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Zahlung konnte nicht gelöscht werden.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title={isPaid ? "Zahlungen" : "Zahlung erfassen"}
      description={
        doc.dokument_nummer
          ? `Rechnung ${doc.dokument_nummer}`
          : "Zahlungen nachvollziehen, ändern oder löschen"
      }
      size="xl"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-2 rounded-xl border border-separator bg-fill-quaternary/40 p-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-label-secondary">Netto</p>
            <p className="font-semibold text-label">{formatCurrency(summeNetto, currency)}</p>
            {summeSteuer !== 0 ? (
              <p className="text-[11px] text-label-tertiary">
                MwSt {formatCurrency(summeSteuer, currency)}
              </p>
            ) : null}
          </div>
          <div>
            <p className="text-xs text-label-secondary">Brutto</p>
            <p className="font-semibold text-label">{formatCurrency(summeBrutto, currency)}</p>
          </div>
          <div>
            <p className="text-xs text-label-secondary">Bezahlt</p>
            <p className="font-semibold text-label">{formatCurrency(summeBezahlt, currency)}</p>
            {summeBezahltNetto != null ? (
              <p className="text-[11px] text-label-tertiary">
                ≈ {formatCurrency(summeBezahltNetto, currency)} netto
              </p>
            ) : null}
          </div>
          <div>
            <p className="text-xs text-label-secondary">Offen · Status</p>
            <p className="font-semibold text-label">
              {formatCurrency(summeOffen, currency)} · {ZAHLUNGSSTATUS_LABELS[zahlungsstatus]}
            </p>
            {summeOffenNetto != null ? (
              <p className="text-[11px] text-label-tertiary">
                ≈ {formatCurrency(summeOffenNetto, currency)} netto
              </p>
            ) : null}
          </div>
        </div>

        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold text-label">Bisherige Zahlungen</h3>
            <p className="text-xs text-label-tertiary">
              {zahlungen.length === 1 ? "1 Eintrag" : `${zahlungen.length} Einträge`}
            </p>
          </div>
          {zahlungen.length === 0 ? (
            <p className="rounded-xl border border-dashed border-separator px-3 py-4 text-sm text-label-secondary">
              Noch keine Zahlung erfasst.
            </p>
          ) : (
            <ul className="space-y-2">
              {zahlungen.map((zahlung) => {
                const zahlungNetto = bruttoToNetto(Number(zahlung.betrag || 0), summeBrutto, summeNetto);
                const lastChange = lastChangeByZahlung.get(zahlung.id);
                const isEditing = editingId === zahlung.id;
                return (
                  <li key={zahlung.id} className="rounded-xl border border-separator bg-card px-3 py-3">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <TextInput
                            label="Betrag (brutto)"
                            value={editBetrag}
                            onChange={(e) => setEditBetrag(e.target.value)}
                            inputMode="decimal"
                          />
                          <TextInput
                            label="Zahlungsdatum"
                            type="date"
                            value={editBezahltAm}
                            onChange={(e) => setEditBezahltAm(e.target.value)}
                          />
                          <TextInput
                            label="Referenz"
                            value={editReferenz}
                            onChange={(e) => setEditReferenz(e.target.value)}
                          />
                          <TextInput
                            label="Notiz"
                            value={editNotiz}
                            onChange={(e) => setEditNotiz(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                          >
                            Abbrechen
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void handleUpdate(zahlung.id)}
                            disabled={!canSaveEdit}
                            loading={saving}
                          >
                            Änderung speichern
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-base font-semibold text-label">
                            {formatCurrency(zahlung.betrag, currency)}
                            <span className="ml-2 text-sm font-normal text-label-secondary">brutto</span>
                            {zahlungNetto != null ? (
                              <span className="ml-2 text-sm font-normal text-label-secondary">
                                · ≈ {formatCurrency(zahlungNetto, currency)} netto
                              </span>
                            ) : null}
                          </p>
                          <p className="mt-0.5 text-sm text-label-secondary">
                            Zahlungsdatum {formatBelegdatum(zahlung.bezahlt_am)}
                          </p>
                          {zahlung.referenz?.trim() ? (
                            <p className="text-xs text-label-secondary">Ref. {zahlung.referenz.trim()}</p>
                          ) : null}
                          {zahlung.notiz?.trim() ? (
                            <p className="text-xs text-label-secondary">{zahlung.notiz.trim()}</p>
                          ) : null}
                          <p className="mt-1 text-[11px] text-label-tertiary">
                            Eingetragen von {personLabel(zahlung.created_by_name, zahlung.created_by)}
                            {zahlung.created_at ? ` am ${formatDateTimeDe(zahlung.created_at)}` : ""}
                          </p>
                          {lastChange ? (
                            <p className="text-[11px] text-status-orange">
                              Zuletzt geändert von {personLabel(lastChange.created_by_name, lastChange.created_by)}
                              {lastChange.created_at ? ` am ${formatDateTimeDe(lastChange.created_at)}` : ""}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(zahlung)}
                            disabled={saving || deletingId != null}
                          >
                            Bearbeiten
                          </Button>
                          {confirmDeleteId === zahlung.id ? (
                            <>
                              <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                loading={deletingId === zahlung.id}
                                disabled={saving || deletingId != null}
                                onClick={() => void handleDelete(zahlung.id)}
                              >
                                Wirklich löschen
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={deletingId != null}
                              >
                                Abbrechen
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setConfirmDeleteId(zahlung.id);
                                setEditingId(null);
                              }}
                              disabled={saving || deletingId != null}
                            >
                              Löschen
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {showComposer ? (
          <section className="space-y-3 rounded-xl border border-separator bg-card p-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-label">
                {zahlungen.length > 0 ? "Weitere Zahlung" : "Zahlung erfassen"}
              </h3>
              {summeOffen <= 0 ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowComposer(false)}>
                  Schließen
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <TextInput
                  label="Betrag (brutto)"
                  value={betrag}
                  onChange={(e) => setBetrag(e.target.value)}
                  inputMode="decimal"
                  placeholder="0,00"
                  hint={
                    summeOffen > 0
                      ? `Offener Bruttobetrag: ${formatCurrency(summeOffen, currency)}`
                      : "Es wird der überwiesene Bruttobetrag erfasst."
                  }
                />
                {parsedBetragNetto != null ? (
                  <p className="rounded-lg border border-separator bg-background px-3 py-2 text-sm text-label">
                    <span className="text-label-secondary">Entspricht netto: </span>
                    <span className="font-semibold">{formatCurrency(parsedBetragNetto, currency)}</span>
                  </p>
                ) : null}
              </div>
              <TextInput
                label="Zahlungsdatum"
                type="date"
                value={bezahltAm}
                onChange={(e) => setBezahltAm(e.target.value)}
              />
              <TextInput
                label="Referenz"
                value={referenz}
                onChange={(e) => setReferenz(e.target.value)}
                placeholder="z. B. Verwendungszweck"
              />
              <TextInput
                label="Notiz"
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
                placeholder="optional"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {summeOffen > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setBetrag(moneyInput(summeOffen))}
                  disabled={saving}
                >
                  Restbetrag übernehmen
                </Button>
              ) : null}
              <Button type="button" size="sm" onClick={() => void handleCreate()} disabled={!canSave} loading={saving}>
                Zahlung speichern
              </Button>
            </div>
          </section>
        ) : (
          <Button type="button" variant="secondary" size="sm" onClick={() => setShowComposer(true)}>
            Weitere Zahlung erfassen
          </Button>
        )}

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-label">Verlauf</h3>
          {protokoll.length === 0 ? (
            <p className="text-sm text-label-secondary">Noch keine Zahlung protokolliert.</p>
          ) : (
            <ol className="space-y-0">
              {protokoll.map((entry, index) => (
                <li key={entry.id} className="relative flex gap-3 pb-4 last:pb-0">
                  {index < protokoll.length - 1 ? (
                    <span className="absolute bottom-0 left-[7px] top-4 w-px bg-separator" aria-hidden />
                  ) : null}
                  <span
                    className={clsx(
                      "relative mt-1.5 h-4 w-4 shrink-0 rounded-full ring-4 ring-background",
                      entry.aktion === "geloescht"
                        ? "bg-status-red"
                        : entry.aktion === "geaendert"
                          ? "bg-status-orange"
                          : "bg-status-green",
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={clsx(
                          "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                          AKTION_BADGE[entry.aktion] || "bg-fill-tertiary text-label-secondary",
                        )}
                      >
                        {AKTION_LABEL[entry.aktion] || entry.aktion}
                      </span>
                      <span className="text-sm font-medium text-label">
                        {personLabel(entry.created_by_name, entry.created_by)}
                      </span>
                      <span className="text-xs text-label-tertiary">
                        {entry.created_at ? formatDateTimeDe(entry.created_at) : ""}
                      </span>
                    </div>
                    {protocolDetail(entry, currency) ? (
                      <p className="mt-1 text-sm text-label-secondary">{protocolDetail(entry, currency)}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </Dialog>
  );
}
